import React, { useState } from 'react';
import { InitialQuotationTableView } from '../components/InitialQuotationTableView';
import { InitialQuotationControlPanel } from '../components/InitialQuotationControlPanel';
import { UpdateInitialQuotationForm } from '../modals/UpdateInitialQuotationForm';
import { InitialQuotationPreviewModal } from '../modals/InitialQuotationPreviewModal';
import { useInitialQuotations } from '../hooks/useInitialQuotations';
import { exportInitialQuotationData } from '@/utils/InitialQuotationExport';
import { ConfirmationModal } from '@/components/ui';
import { showSuccessMessage, showApiError } from '@/lib/toast';
import { getCumulativeStatuses } from '@/constants/globalConstants';
import { generateRoomQuotationPdf } from '@/utils/generateRoomQuotationPdf';
import { fmt } from '@/lib/formatters';

export const InitialQuotationScreen = ({ filters: globalFilters = {}, onLeadClick }) => {
  // Local state for isolated functionality
  const [localSearch, setLocalSearch] = useState('');
  const [localFilters, setLocalFilters] = useState({
    date: 'all',
    fromDate: '',
    toDate: ''
  });

  const [selectedInitialQuotation, setSelectedInitialQuotation] = useState(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewLead, setPreviewLead] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false });

  // Merge global filters (status = initial_quotation) with local search & date filters
  // Calculate fromDate and toDate correctly based on presets
  const calculateDateRange = () => {
    const filtersToUse = { ...localFilters };
    if (filtersToUse.date && filtersToUse.date !== 'all') {
      const now = new Date();
      let fromDate = new Date();
      
      switch(filtersToUse.date) {
        case 'today':
          break;
        case 'week':
          fromDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          fromDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          fromDate.setFullYear(now.getFullYear() - 1);
          break;
      }
      
      filtersToUse.fromDate = fromDate.toISOString().split('T')[0];
      filtersToUse.toDate = now.toISOString().split('T')[0];
    }
    return filtersToUse;
  };

  const combinedFilters = {
    ...globalFilters,
    search: localSearch,
    ...calculateDateRange(),
    status: getCumulativeStatuses('initial_quotation')
  };

  const {
    initialQuotations,
    isLoading,
    updateInitialQuotation,
    deleteInitialQuotation,
    toggleDisableInitialQuotation,
    refetch
  } = useInitialQuotations(combinedFilters);

  const handleEditClick = (iq) => {
    setSelectedInitialQuotation(iq);
    setIsUpdateModalOpen(true);
  };

  const handleViewClick = (iq) => {
    setPreviewLead(iq);
    setIsPreviewModalOpen(true);
  };

  const handleDeleteClick = (iq) => {
    const isEnabling = iq.isDisabled;
    setConfirmState({
      isOpen: true,
      title: isEnabling ? "Enable Record" : "Disable Record",
      message: `Are you sure you want to ${isEnabling ? 'enable' : 'disable'} this initial quotation?`,
      variant: isEnabling ? "info" : "danger",
      confirmText: isEnabling ? "Enable" : "Disable",
      onConfirm: async () => {
        setConfirmState(prev => ({ ...prev, loading: true }));
        try {
          await toggleDisableInitialQuotation({ id: iq._id, isDisabled: !iq.isDisabled });
          showSuccessMessage(`Record ${isEnabling ? 'enabled' : 'disabled'} successfully`);
          refetch();
        } catch (err) {
          showApiError(err);
        } finally {
          setConfirmState({ isOpen: false });
        }
      }
    });
  };

  const handleExport = async () => {
    if (!initialQuotations || initialQuotations.length === 0) {
      showApiError(new Error("No initial quotation records available to export."));
      return;
    }
    
    setIsExporting(true);
    try {
      await exportInitialQuotationData(initialQuotations);
      showSuccessMessage("Export completed successfully");
    } catch (err) {
      showApiError(err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadPdfClick = async (lead) => {
    try {
      // Re-create templateData logic from Preview
      let savedItems = lead.items || lead.quotationItems || lead.initialQuotationItems || [];
      if (lead.quotationData && lead.quotationData.items) savedItems = lead.quotationData.items;

      const roomsData = savedItems.map(roomData => ({
        roomName: roomData.room || 'Room',
        items: (roomData.items || []).map(item => ({
          name: item.name,
          colour: item.color || item.colour || '-',
          type: item.category === 'internalType' ? 'Internal Type' : 
                item.category === 'shutterType' ? 'Shutter Type' : 
                item.category === 'blumItems' ? 'Hardware' : 
                item.category === 'customItems' ? 'Custom' : item.category || item.type || '-',
          warranty: item.warranty || '-',
          amount: fmt(parseFloat(item.unitPrice || item.amount || 0)),
          details: item.measurementInfo ? item.measurementInfo.split(' | ') : [],
          quantity: item.quantity || 1
        }))
      }));

      const amount = lead.quotationAmount || lead.amount || 0;
      const templateData = {
        projectName: lead.projectName || "-",
        clientName: lead.customerName || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || "-",
        date: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
        site: lead.city || lead.location || "-",
        quotationNo: lead.customId ? `VK/${lead.customId.toUpperCase()}` : "-",
        contactRef: lead.phoneNumber || lead.phone || "-",
        designer: lead.projectManager?.name || lead.assignedTo?.name || "-",
        rooms: roomsData,
        totals: {
          total: fmt(amount),
          discount: "0",
          grandTotal: fmt(amount)
        }
      };

      await generateRoomQuotationPdf(lead, templateData);
      showSuccessMessage("Quotation PDF Downloaded!");
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <div className="flex flex-col h-full w-full">
      <InitialQuotationControlPanel
        search={localSearch}
        setSearch={setLocalSearch}
        filters={localFilters}
        setFilters={setLocalFilters}
        onExport={handleExport}
        isExporting={isExporting}
      />
      
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden">
        <InitialQuotationTableView
          leads={initialQuotations}
          isLoading={isLoading}
          onLeadClick={(iq) => {
            if (onLeadClick) onLeadClick(iq);
          }}
          onViewClick={handleViewClick}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          onDownloadClick={handleDownloadPdfClick}
        />
      </div>

      {isUpdateModalOpen && (
        <UpdateInitialQuotationForm
          isOpen={isUpdateModalOpen}
          onClose={() => {
            setIsUpdateModalOpen(false);
            setSelectedInitialQuotation(null);
          }}
          initialData={selectedInitialQuotation}
          onSubmit={async (id, data) => {
            await updateInitialQuotation({ id, data });
            showSuccessMessage("Initial Quotation updated successfully");
            refetch();
          }}
        />
      )}

      {isPreviewModalOpen && (
        <InitialQuotationPreviewModal
          isOpen={isPreviewModalOpen}
          onClose={() => {
            setIsPreviewModalOpen(false);
            setPreviewLead(null);
          }}
          lead={previewLead}
        />
      )}

      {confirmState.isOpen && (
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          onClose={() => setConfirmState({ isOpen: false })}
          title={confirmState.title}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          variant={confirmState.variant}
          confirmText={confirmState.confirmText}
          loading={confirmState.loading}
        />
      )}
    </div>
  );
};
